/**
 * useRaceDebugEventLog — wires EventBus subscriptions for all race:* and
 * gmEvent* events, pushing them into the RaceDebugOverlayState rolling log.
 *
 * Extracted from RaceDebugOverlay so the subscription list is testable in
 * isolation and the main component stays a pure render.
 *
 * Installs once on mount; tears down all listeners on unmount.
 */

import { useEffect, useRef } from 'react';
import { EventBus } from '../../../../shared/EventBus';
import { pushRaceDebugEvent } from '../../../systems/race/RaceDebugOverlayState';

/** Server→client relay events (the `net:message:` prefix is stripped in the log). */
const RELAY_EVENTS: string[] = [
  'net:message:race:hazardHit',
  'net:message:race:hazardImpulse',
  'net:message:race:powerupPicked',
  'net:message:race:powerupEffectStart',
  'net:message:race:powerupEffectEnd',
  'net:message:race:slowTrapPlaced',
  'net:message:race:slowTrapTriggered',
  'net:message:race:gateOpen',
  'net:message:race:dnf',
  'net:message:race:abilityGate',
  'net:message:race:abilityGateClear',
  'net:message:race:statOverride',
  'net:message:race:statRestore',
  'net:message:gmEventAnnouncement',
  'net:message:gmEventPhaseChanged',
  'net:message:gmEventRaceFinish',
  'net:message:gmEventRacePlayerState',
  'net:message:gmEventEnded',
  'net:message:gmEventCancelled',
];

/** Client-internal race events (no prefix strip). */
const INTERNAL_EVENTS: string[] = [
  'race:checkpointReached',
  'race:countdownTick',
  'race:countdownGo',
  'race:finished',
  'race:hazardImpulseApplied',
];

function summarize(data: unknown): string {
  if (typeof data === 'object' && data) return JSON.stringify(data).slice(0, 80);
  return String(data ?? '');
}

export function useRaceDebugEventLog(): void {
  const wiredRef = useRef(false);

  useEffect(() => {
    if (wiredRef.current) return;
    wiredRef.current = true;

    const bus = EventBus.getInstance();
    const unsubs: Array<() => void> = [];

    for (const evt of RELAY_EVENTS) {
      unsubs.push(
        bus.on(evt as any, (...args: any[]) => {
          pushRaceDebugEvent(evt.replace('net:message:', ''), summarize(args[0]));
        }),
      );
    }

    for (const evt of INTERNAL_EVENTS) {
      unsubs.push(
        bus.on(evt as any, (...args: any[]) => {
          pushRaceDebugEvent(evt, summarize(args[0]));
        }),
      );
    }

    return () => { unsubs.forEach((u) => u()); };
  }, []);
}
